import isEmpty from 'lodash/isEmpty';
import L from 'leaflet';
import { colors } from 'global/variables';
import {
  createOrderClusterMarkerMultipleSvg,
  createStopClusterMarkerDoubleSvg,
  createStopClusterMarkerMultipleSvg,
  createOrderClusterMarkerDoubleSvg,
  createParkingClusterMarkerDoubleSvg,
  createParkingClusterMarkerMultipleSvg,
  createRechargeClusterMarkerDoubleSvg,
  createRechargeClusterMarkerMultipleSvg,
  getSpecialStopClusterIcon,
} from '../../icons/marker';
import { isActivityToDisplay } from './MapFeatureFilters';
import { getTitle } from './MapMarkersUtils';

const SPECIAL_STOP_TYPES = [
  'break',
  'reload',
  'recharge',
  'pudo',
  'drivingRestTime',
  'workingRestTime',
  'dutyWorkingRestTime',
  'dutyDrivingRestTime',
];

const MIXED_TYPES_COLOR = { inner: colors.grey, outer: colors.grey, text: colors.white };

const getClusterMarkerSvg = (
  type,
  additionalClass,
  jobMarkerColor,
  displayInfo,
  jobType,
  highlight,
  highPriority,
  isParking,
  specialIcon,
  isMixedSpecial,
  isRecharge,
) => {
  let colorToUse;

  if (type === 'activity') {
    colorToUse = isMixedSpecial
      ? MIXED_TYPES_COLOR
      : jobType === 'pickup'
      ? jobMarkerColor.pickup
      : jobMarkerColor.delivery;
    return createStopClusterMarkerDoubleSvg(
      colorToUse.inner,
      highlight ? 'yellow' : highPriority ? 'orange' : colorToUse.outer,
      14,
      20,
      displayInfo,
      colorToUse.text,
      '',
    );
  }
  if (type === 'stop') {
    if (isParking) {
      if (additionalClass === 'double') {
        return createParkingClusterMarkerDoubleSvg(40, 46);
      }
      return createParkingClusterMarkerMultipleSvg(40, 46);
    }
    if (isRecharge) {
      if (additionalClass === 'double') {
        return createRechargeClusterMarkerDoubleSvg(40, 46);
      }
      return createRechargeClusterMarkerMultipleSvg(40, 46);
    }
    colorToUse = isMixedSpecial
      ? MIXED_TYPES_COLOR
      : jobType === 'pickup'
      ? jobMarkerColor.pickup
      : jobMarkerColor.delivery;

    if (additionalClass === 'double') {
      return createStopClusterMarkerDoubleSvg(
        colorToUse.inner,
        highlight ? 'yellow' : highPriority ? 'orange' : colorToUse.outer,
        40,
        46,
        specialIcon ? '' : displayInfo,
        colorToUse.text,
        specialIcon,
      );
    }
    return createStopClusterMarkerMultipleSvg(
      colorToUse.inner,
      highlight ? 'yellow' : highPriority ? 'orange' : colorToUse.outer,
      40,
      46,
      specialIcon ? '' : displayInfo,
      colorToUse.text,
      specialIcon,
    );
  }
  if (additionalClass === 'double') {
    return createOrderClusterMarkerDoubleSvg(40, 46, specialIcon ? '' : displayInfo, highPriority && 'orange', specialIcon);
  }
  return createOrderClusterMarkerMultipleSvg(40, 46, specialIcon ? '' : displayInfo, highPriority && 'orange', specialIcon);
};

const CLUSTER_POPUP_OPTIONS = { maxWidth: 200 };

const wrapScrollable = (content) =>
  `<div style="max-height:160px;overflow-y:auto;font-size:12px;line-height:1.4">${content}</div>`;

const getActivityClusterPopupContent = (labels, translations) => {
  const header = `<b style="font-size:12px">${translations.wizard.orders.activityCount}: ${labels.length}</b>`;
  return wrapScrollable(`${header}${getTitle(translations, labels)}`);
};

export const getStopClusterPopupContent = (tmpClusters, translations) => {
  const { batteryChargeTrans, consumptionTrans } = translations.map;
  const lines = tmpClusters.map((c) => {
    const p = c.feature.properties;
    const stopNum = p.jobOrder ?? p.stopIndex;
    const title = getTitle(
      translations,
      p.labels,
      (p.types || []).reduce((acc, t) => {
        acc[t] = 1;
        return acc;
      }, {}),
      p.highPriority,
      p.address,
    );
    const label = title || (p.address ? `<div class="labels">${p.address}</div>` : '');
    const chargePercent =
      p.chargeKwh != null && p.batteryCapacity != null && p.batteryCapacity > 0
        ? Math.round((p.chargeKwh / p.batteryCapacity) * 100)
        : null;
    const batteryChargeLine =
      chargePercent != null
        ? `<div title="${batteryChargeTrans}">${batteryChargeTrans}: ${chargePercent}%</div>`
        : '';
    const consumptionLine =
      p.consumption != null
        ? `<div title="${consumptionTrans}">${consumptionTrans}: ${p.consumption}</div>`
        : '';
    const header = stopNum != null ? `<div><b>#${stopNum}</b>${label ? ` ${label}` : ''}</div>` : label;
    return `${header}${batteryChargeLine}${consumptionLine}`;
  });
  return wrapScrollable(lines.join(''));
};

let _activeClusterPopup = null;
let _clusterCloseTimer = null;
let _isClusterPopupHovered = false;

const cancelClusterClose = () => {
  if (_clusterCloseTimer) {
    clearTimeout(_clusterCloseTimer);
    _clusterCloseTimer = null;
  }
};

const closeClusterPopup = (map) => {
  if (_isClusterPopupHovered) return;
  if (_activeClusterPopup) {
    map.closePopup(_activeClusterPopup);
    _activeClusterPopup = null;
  }
};

const scheduleClusterClose = (map) => {
  cancelClusterClose();
  _clusterCloseTimer = setTimeout(() => closeClusterPopup(map), 300);
};

const attachClusterPopupListeners = (map) => {
  const el = _activeClusterPopup?.getElement();
  if (!el) return;
  el.addEventListener('mouseenter', () => {
    _isClusterPopupHovered = true;
    cancelClusterClose();
  });
  el.addEventListener('mouseleave', () => {
    _isClusterPopupHovered = false;
    scheduleClusterClose(map);
  });
};

const bindHoverPopup = (cluster, map, getContent) => {
  if (cluster._hoverPopupBound) return;
  cluster._hoverPopupBound = true;

  cluster.on('mouseover', (c) => {
    cancelClusterClose();
    if (_activeClusterPopup) {
      map.closePopup(_activeClusterPopup);
    }
    _isClusterPopupHovered = false;
    _activeClusterPopup = L.popup(CLUSTER_POPUP_OPTIONS)
      .setLatLng(c.latlng)
      .setContent(getContent())
      .openOn(map);
    setTimeout(() => attachClusterPopupListeners(map), 0);
  });

  cluster.on('mouseout', () => scheduleClusterClose(map));
};

export const createClusterCustomIcon = (cluster, highlight, map, translations, highlightedJobIds) => {
  const childs = cluster.getAllChildMarkers();
  const labels = [];
  childs.forEach((c) => {
    if (c.feature.properties.labels) labels.push(c.feature.properties.labels[0]);
  });
  if (childs.every((child) => isActivityToDisplay(child.feature))) {
    bindHoverPopup(cluster, map, () => getActivityClusterPopupContent(labels, translations));
  }

  const tmpClusters = cluster.getAllChildMarkers();
  tmpClusters.sort((a, b) => {
    const aOrder =
      a.feature.properties.jobOrder ?? a.feature.properties.stopIndex ?? Number.MAX_SAFE_INTEGER;
    const bOrder =
      b.feature.properties.jobOrder ?? b.feature.properties.stopIndex ?? Number.MAX_SAFE_INTEGER;
    return aOrder - bOrder;
  });
  const childrenForRoute = tmpClusters.filter((c) => c.feature.properties.routeId !== undefined);
  const isSolution = !isEmpty(childrenForRoute);

  if (isSolution) {
    bindHoverPopup(cluster, map, () => getStopClusterPopupContent(tmpClusters, translations));
  }
  const isActivity = isActivityToDisplay(tmpClusters[0].feature);
  const featureProperties = tmpClusters[0].feature.properties;
  const hasUnassigned = featureProperties.reason && featureProperties.reason.length > 0;
  const highlighted =
    highlight.id &&
    tmpClusters.some(
      (tmp) =>
        highlight.tourId === tmp.feature.properties.routeId &&
        ((tmp.feature.properties.jobOrder && tmp.feature.properties.jobOrder === highlight.id) ||
          (tmp.feature.properties.stopIndex && tmp.feature.properties.stopIndex === highlight.id) ||
          (highlightedJobIds && tmp.feature.properties.jobIds &&
            tmp.feature.properties.jobIds.some((jid) => highlightedJobIds.has(jid)))),
    );

  const highPriority = tmpClusters.some((tmp) => tmp.feature.properties.highPriority);
  const isParking = tmpClusters.every((tmp) => tmp.feature.properties.types?.includes('parking'));
  const isRecharge = tmpClusters.every((tmp) => tmp.feature.properties.types?.includes('recharge'));

  const allClusterTypes = tmpClusters.flatMap((tmp) => tmp.feature.properties.types || []);
  const uniqueTypes = [...new Set(allClusterTypes)];
  const isUniformSpecialType =
    !isParking && !isRecharge && uniqueTypes.length === 1 && SPECIAL_STOP_TYPES.includes(uniqueTypes[0]);
  const isMixedSpecial =
    !isParking && !isRecharge && !isUniformSpecialType && uniqueTypes.some((t) => SPECIAL_STOP_TYPES.includes(t));
  const specialIcon = isUniformSpecialType ? getSpecialStopClusterIcon(uniqueTypes[0]) : '';

  const type = hasUnassigned
    ? 'unassigned'
    : isActivity
    ? 'activity'
    : isSolution
    ? 'stop'
    : 'order';
  const displayInfo = isActivity
    ? ''
    : isSolution
    ? featureProperties.jobOrder ?? featureProperties.stopIndex ?? cluster.getChildCount()
    : cluster.getChildCount();

  const jobMarkerColor = featureProperties.jobMarkerColor;
  const jobType = isUniformSpecialType ? uniqueTypes[0] : featureProperties?.types?.[0];
  const additionalClass = cluster.getChildCount() === 2 ? 'double' : 'multiple';
  const options = {
    html: L.Util.template(
      getClusterMarkerSvg(
        type,
        additionalClass,
        jobMarkerColor,
        displayInfo,
        jobType,
        highlighted,
        highPriority,
        isParking,
        specialIcon,
        isMixedSpecial,
        isRecharge,
      ),
    ),
    className: `marker-cluster marker-cluster-${
      type === 'activity' ? 'activity' : isParking ? 'parking' : isRecharge ? 'recharge' : type
    } marker-cluster-${additionalClass}`,
    iconAnchor: isActivity ? [7, 10] : [20, 23],
  };

  return new L.DivIcon(options);
};
